import { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import {
  BadgeCheck,
  Banknote,
  Bell,
  FileText,
  Home,
  Landmark,
  LayoutDashboard,
  Receipt,
  ScrollText,
  Send,
  Settings as SettingsIcon,
  ShieldCheck,
  UserCircle,
  Users,
  Wallet,
} from 'lucide-react';
import { PERMISSIONS } from '@unifemga/shared';

import { AuthProvider, RequireAuth } from '@/lib/auth';
import { AppShell, type NavItem } from '@/layouts/AppShell';

import { PublicShell } from '@/layouts/PublicShell';
import PublicHome from '@/pages/public/Home';
import PublicLoans from '@/pages/public/Loans';
import PublicAbout from '@/pages/public/About';
import PublicContact from '@/pages/public/Contact';
import PublicFaq from '@/pages/public/Faq';
import PublicPrivacy from '@/pages/public/Privacy';
import PublicTerms from '@/pages/public/Terms';
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import TwoFactorSetup from '@/pages/auth/TwoFactorSetup';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import VerifyEmail from '@/pages/auth/VerifyEmail';
import ResetPassword from '@/pages/auth/ResetPassword';

import PortalDashboard from '@/pages/portal/Dashboard';
import MyLoans from '@/pages/portal/MyLoans';
import ApplyForLoan from '@/pages/portal/ApplyForLoan';
import MemberLoanDetail from '@/pages/portal/LoanDetail';
import GuaranteedLoans from '@/pages/portal/GuarantorRequests';
import MemberKyc from '@/pages/portal/Kyc';
import MemberProfile from '@/pages/portal/Profile';
import MyApplications from '@/pages/portal/Applications';
import BankAccounts from '@/pages/portal/BankAccounts';
import MemberNotifications from '@/pages/portal/Notifications';
import Settings from '@/pages/portal/Settings';

import AdminDashboard from '@/pages/admin/Dashboard';
import AdminMembers from '@/pages/admin/Members';
import AdminKycReview from '@/pages/admin/KycReview';
import AdminApplications from '@/pages/admin/Applications';
import AdminDisbursements from '@/pages/admin/Disbursements';
import AdminRepayments from '@/pages/admin/Repayments';
import AdminLoanProducts from '@/pages/admin/LoanProducts';
import AdminNotifications from '@/pages/admin/Notifications';
import AdminAuditLog from '@/pages/admin/AuditLog';
import AdminSettings from '@/pages/admin/Settings';
import AdminMemberDetail from '@/pages/admin/MemberDetail';
import AdminApplicationDetail from '@/pages/admin/ApplicationDetail';
import { AdminLoans, AdminLoanDetail } from '@/pages/admin/Loans';

const MEMBER_NAV: NavItem[] = [
  { to: '/portal', label: 'Dashboard', icon: LayoutDashboard, end: true },

  { to: '/portal/loans', label: 'My loans', icon: Wallet, group: 'Borrowing' },
  { to: '/portal/applications', label: 'Applications', icon: FileText, group: 'Borrowing' },
  { to: '/portal/guarantors', label: 'Loans you guarantee', icon: ShieldCheck, group: 'Borrowing' },

  { to: '/portal/bank-accounts', label: 'Bank accounts', icon: Landmark, group: 'Account' },
  { to: '/portal/kyc', label: 'Verification', icon: BadgeCheck, group: 'Account' },
  { to: '/portal/notifications', label: 'Notifications', icon: Bell, group: 'Account' },
  { to: '/portal/profile', label: 'Profile', icon: UserCircle, group: 'Account' },
  { to: '/portal/settings', label: 'Settings', icon: SettingsIcon, group: 'Account' },
];

const ADMIN_NAV: NavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },

  { to: '/admin/members', label: 'Members', icon: Users, permission: PERMISSIONS.MEMBERS_VIEW, group: 'People' },
  { to: '/admin/kyc', label: 'KYC review', icon: BadgeCheck, permission: PERMISSIONS.KYC_VIEW, group: 'People' },

  {
    to: '/admin/applications',
    label: 'Applications',
    icon: FileText,
    permission: PERMISSIONS.APPLICATIONS_VIEW,
    group: 'Lending',
  },
  { to: '/admin/loans', label: 'Loans', icon: Wallet, permission: PERMISSIONS.LOANS_VIEW, group: 'Lending' },
  {
    to: '/admin/disbursements',
    label: 'Disbursements',
    icon: Send,
    permission: PERMISSIONS.DISBURSEMENTS_VIEW,
    group: 'Lending',
  },
  {
    to: '/admin/repayments',
    label: 'Repayments',
    icon: Receipt,
    permission: PERMISSIONS.REPAYMENTS_VIEW,
    group: 'Lending',
  },

  {
    to: '/admin/loan-products',
    label: 'Loan products',
    icon: Banknote,
    permission: PERMISSIONS.LOAN_PRODUCTS_VIEW,
    group: 'Configuration',
  },
  {
    to: '/admin/notifications',
    label: 'Notifications',
    icon: Bell,
    permission: PERMISSIONS.NOTIFICATIONS_VIEW,
    group: 'Configuration',
  },
  {
    to: '/admin/audit',
    label: 'Audit trail',
    icon: ScrollText,
    permission: PERMISSIONS.AUDIT_VIEW,
    group: 'Configuration',
  },
  {
    to: '/admin/settings',
    label: 'Settings',
    icon: SettingsIcon,
    permission: PERMISSIONS.SETTINGS_VIEW,
    group: 'Configuration',
  },
];

/**
 * Scrolls to the top on navigation.
 *
 * A single-page router keeps the scroll offset between routes, so following a
 * footer link from halfway down the home page lands you halfway down the new
 * one. Hash links are left alone so in-page anchors still work.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <ScrollToTop />
      <Routes>
        {/* Public */}
        {/* Marketing site — one shell, so the header and footer cannot drift. */}
        <Route element={<PublicShell />}>
          <Route path="/" element={<PublicHome />} />
          <Route path="/loans" element={<PublicLoans />} />
          <Route path="/about" element={<PublicAbout />} />
          <Route path="/contact" element={<PublicContact />} />
          <Route path="/faq" element={<PublicFaq />} />
          <Route path="/privacy" element={<PublicPrivacy />} />
          <Route path="/terms" element={<PublicTerms />} />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* Reachable with an enrolment-scoped token, which is all an
            administrator holds until 2FA setup is complete. */}
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/two-factor-setup" element={<TwoFactorSetup />} />

        {/* Member portal */}
        <Route
          path="/portal"
          element={
            <RequireAuth requireMember>
              <AppShell nav={MEMBER_NAV} title="Member portal" homePath="/portal" />
            </RequireAuth>
          }
        >
          <Route index element={<PortalDashboard />} />
          <Route path="loans" element={<MyLoans />} />
          <Route path="loans/apply" element={<ApplyForLoan />} />
          <Route path="loans/:id" element={<MemberLoanDetail />} />
          <Route path="applications" element={<MyApplications />} />
          <Route path="guarantors" element={<GuaranteedLoans />} />
          <Route path="bank-accounts" element={<BankAccounts />} />
          <Route path="kyc" element={<MemberKyc />} />
          <Route path="notifications" element={<MemberNotifications />} />
          <Route path="profile" element={<MemberProfile />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        {/* Admin portal. Each route also carries the permission its navigation
            entry checks, so reaching one by URL is guarded too. */}
        <Route
          path="/admin"
          element={
            <RequireAuth requireAdmin>
              <AppShell nav={ADMIN_NAV} title="Administration" homePath="/admin" />
            </RequireAuth>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route
            path="members"
            element={
              <RequireAuth permission={PERMISSIONS.MEMBERS_VIEW}>
                <AdminMembers />
              </RequireAuth>
            }
          />
          <Route
            path="kyc"
            element={
              <RequireAuth permission={PERMISSIONS.KYC_VIEW}>
                <AdminKycReview />
              </RequireAuth>
            }
          />
          <Route
            path="applications"
            element={
              <RequireAuth permission={PERMISSIONS.APPLICATIONS_VIEW}>
                <AdminApplications />
              </RequireAuth>
            }
          />
          <Route
            path="loans"
            element={
              <RequireAuth permission={PERMISSIONS.LOANS_VIEW}>
                <AdminLoans />
              </RequireAuth>
            }
          />
          <Route
            path="loans/:id"
            element={
              <RequireAuth permission={PERMISSIONS.LOANS_VIEW}>
                <AdminLoanDetail />
              </RequireAuth>
            }
          />
          <Route
            path="disbursements"
            element={
              <RequireAuth permission={PERMISSIONS.DISBURSEMENTS_VIEW}>
                <AdminDisbursements />
              </RequireAuth>
            }
          />
          <Route
            path="repayments"
            element={
              <RequireAuth permission={PERMISSIONS.REPAYMENTS_VIEW}>
                <AdminRepayments />
              </RequireAuth>
            }
          />
          <Route
            path="loan-products"
            element={
              <RequireAuth permission={PERMISSIONS.LOAN_PRODUCTS_VIEW}>
                <AdminLoanProducts />
              </RequireAuth>
            }
          />
          <Route
            path="notifications"
            element={
              <RequireAuth permission={PERMISSIONS.NOTIFICATIONS_VIEW}>
                <AdminNotifications />
              </RequireAuth>
            }
          />
          <Route
            path="audit"
            element={
              <RequireAuth permission={PERMISSIONS.AUDIT_VIEW}>
                <AdminAuditLog />
              </RequireAuth>
            }
          />
          <Route
            path="members/:id"
            element={
              <RequireAuth permission={PERMISSIONS.MEMBERS_VIEW}>
                <AdminMemberDetail />
              </RequireAuth>
            }
          />
          <Route
            path="applications/:id"
            element={
              <RequireAuth permission={PERMISSIONS.APPLICATIONS_VIEW}>
                <AdminApplicationDetail />
              </RequireAuth>
            }
          />
          <Route
            path="settings"
            element={
              <RequireAuth permission={PERMISSIONS.SETTINGS_VIEW}>
                <AdminSettings />
              </RequireAuth>
            }
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center">
      <p className="wordmark mb-6 text-3xl text-foreground">AAF-UNIFEMGA</p>
      <h1 className="mb-3 font-heading text-5xl font-black text-foreground">404</h1>
      <p className="mb-8 text-foreground-secondary">We could not find that page.</p>
      <a
        href="/"
        className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-bold text-primary-foreground shadow-lg shadow-primary/20 transition-transform hover:-translate-y-0.5"
      >
        <Home size={16} /> Back to home
      </a>
    </div>
  );
}
